import imageCompression from 'browser-image-compression';

export class ImageCompressionService {
  static MAX_DIMENSION = 2048;
  static TARGET_SIZE_KB = 800; // Target ~800KB for good upload/OCR performance

  // Quality thresholds
  static QUALITY_HIGH = 0.9;
  static QUALITY_MEDIUM = 0.75;
  static QUALITY_LOW = 0.6;

  /**
   * Fast image quality analysis using image size and file size heuristics
   * Much faster than pixel-by-pixel analysis
   */
  static async analyzeImageQuality(file) {
    return new Promise(resolve => {
      const img = new Image();

      img.onload = () => {
        try {
          const sizeMB = file.size / (1024 * 1024);
          const megapixels = (img.width * img.height) / 1000000;
          const compressionRatio = file.size / (img.width * img.height * 3); // Assume 3 bytes per pixel for uncompressed

          // Heuristic quality score based on file characteristics
          let qualityScore = 100;

          // Large file size + high resolution = likely high quality
          if (sizeMB > 2 && megapixels > 4) {
            qualityScore = 300; // High quality
          }
          // Medium file size or resolution
          else if (sizeMB > 1 || megapixels > 2) {
            qualityScore = 150; // Medium quality
          }
          // Small file size + low compression ratio = possibly poor quality
          else if (compressionRatio < 0.1) {
            qualityScore = 50; // Potentially low quality, preserve more
          }

          console.log(
            `[IMAGE] Fast quality analysis: ${qualityScore.toFixed(0)} (${sizeMB.toFixed(1)}MB, ${megapixels.toFixed(
              1
            )}MP)`
          );
          resolve(qualityScore);
        } catch (error) {
          console.warn('Failed to analyze image quality:', error);
          resolve(100); // Default moderate quality
        }
      };

      img.onerror = () => {
        console.warn('Failed to load image for quality analysis');
        resolve(100);
      };

      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Determines compression strategy based on image characteristics
   */
  static async determineCompressionStrategy(file) {
    const qualityScore = await this.analyzeImageQuality(file);
    const sizeMB = file.size / (1024 * 1024);

    console.log(`[IMAGE] Quality score: ${qualityScore.toFixed(2)}, Size: ${sizeMB.toFixed(2)}MB`);

    // High quality (clear image) - can compress more aggressively
    if (qualityScore > 200) {
      return {
        quality: sizeMB > 3 ? this.QUALITY_LOW : this.QUALITY_MEDIUM,
        maxSizeMB: sizeMB > 2 ? 1.2 : 1.8,
        strategy: 'aggressive',
      };
    }

    // Medium quality - moderate compression
    if (qualityScore > 80) {
      return {
        quality: sizeMB > 4 ? this.QUALITY_MEDIUM : this.QUALITY_HIGH,
        maxSizeMB: sizeMB > 3 ? 1.8 : 2.5,
        strategy: 'moderate',
      };
    }

    // Low quality (blurry/poor quality) - minimal compression to preserve OCR accuracy
    return {
      quality: this.QUALITY_HIGH,
      maxSizeMB: sizeMB > 5 ? 2.5 : sizeMB, // Only compress if extremely large
      strategy: 'minimal',
    };
  }

  /**
   * Compresses image with smart quality detection - optimized for speed
   */
  static async compressImage(file) {
    const startTime = Date.now();
    const originalSize = file.size;

    try {
      console.log(`[IMAGE] Starting compression for ${file.name} (${(originalSize / 1024 / 1024).toFixed(2)}MB)`);

      // Quick file size check - skip compression for small files
      if (originalSize < 500 * 1024) {
        // Less than 500KB
        console.log('[IMAGE] File is already small, skipping compression');
        return {
          file: file,
          originalSize,
          compressedSize: originalSize,
          compressionRatio: 0,
          qualityDecision: 'skipped',
          processingTime: Date.now() - startTime,
        };
      }

      // Analyze image and determine strategy
      const strategy = await this.determineCompressionStrategy(file);

      console.log(
        `[IMAGE] Using ${strategy.strategy} compression strategy (quality: ${strategy.quality}, maxSize: ${strategy.maxSizeMB}MB)`
      );

      // Optimized compression options for speed
      const options = {
        maxSizeMB: strategy.maxSizeMB,
        maxWidthOrHeight: this.MAX_DIMENSION,
        useWebWorker: false, // Disable web worker for faster small files
        quality: strategy.quality,
        initialQuality: strategy.quality,
        alwaysKeepResolution: false,
        fileType: 'image/jpeg', // Force JPEG for better compression
      };

      const compressedFile = await imageCompression(file, options);

      const compressionRatio = ((originalSize - compressedFile.size) / originalSize) * 100;
      const processingTime = Date.now() - startTime;

      console.log(`[IMAGE] Compression completed in ${processingTime}ms:`);
      console.log(
        `[IMAGE] Original: ${(originalSize / 1024 / 1024).toFixed(2)}MB → Compressed: ${(
          compressedFile.size /
          1024 /
          1024
        ).toFixed(2)}MB`
      );
      console.log(`[IMAGE] Compression: ${compressionRatio.toFixed(1)}% reduction (${strategy.strategy} strategy)`);

      return {
        file: compressedFile,
        originalSize,
        compressedSize: compressedFile.size,
        compressionRatio,
        qualityDecision: strategy.strategy,
        processingTime,
      };
    } catch (error) {
      console.error('Image compression failed:', error);
      console.log('[IMAGE] Falling back to original image');

      // Return original file if compression fails
      return {
        file: file,
        originalSize,
        compressedSize: originalSize,
        compressionRatio: 0,
        qualityDecision: 'error',
        processingTime: Date.now() - startTime,
        error: error.message,
      };
    }
  }

  /**
   * Quick compression for camera captures (always compress since they're typically large)
   */
  static async compressCameraCapture(blob) {
    const startTime = Date.now();

    try {
      // Camera captures are typically high quality, so we can be more aggressive
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: this.MAX_DIMENSION,
        useWebWorker: false, // Faster for typical camera sizes
        quality: 0.8,
        initialQuality: 0.8,
        fileType: 'image/jpeg',
      };

      const compressedFile = await imageCompression(blob, options);

      const compressionRatio = ((blob.size - compressedFile.size) / blob.size) * 100;
      const processingTime = Date.now() - startTime;

      console.log(`[IMAGE] Camera capture compressed in ${processingTime}ms:`);
      console.log(
        `[IMAGE] Original: ${(blob.size / 1024 / 1024).toFixed(2)}MB → Compressed: ${(
          compressedFile.size /
          1024 /
          1024
        ).toFixed(2)}MB`
      );
      console.log(`[IMAGE] Compression: ${compressionRatio.toFixed(1)}% reduction`);

      return compressedFile;
    } catch (error) {
      console.error('Camera capture compression failed:', error);
      return blob; // Return original if compression fails
    }
  }
}
